import { Alert, Button, Layout, Space, Tag } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';

import {
    BackToModesLink,
    CORRECT_SCORE_THRESHOLD,
    Header,
    KRAJ_NAMES,
    MapFrame,
    ModalMapPreview,
    OptionButton,
    OptionGrid,
    Page,
    Question,
    QuizFinalResult,
    QuizPanel,
    QuizPhotoPreview,
    RoundResultModal,
    RoundStatus,
    SvgMap,
    calculateBlindMapScore,
    calculateDistanceKm,
    formatDistance,
    makeOptions,
    shuffleArray,
    useKrajFeatures,
} from '../../components/quiz';
import unescoData from '../../data/unesco.json';

const { Content } = Layout;

const ITEMS = unescoData.items;
const ALL_NAMES = ITEMS.map(item => item.nazev);
const TOTAL_ROUNDS = ITEMS.length;

const PhotoFrame = styled.figure`
    margin: 0;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    overflow: hidden;
    background: #f8fafc;
`;

const PhotoImage = styled.img`
    display: block;
    width: 100%;
    max-height: 460px;
    object-fit: cover;
    background: #eee;
`;

const ModalPhoto = styled.img`
    display: block;
    width: 100%;
    max-height: 240px;
    object-fit: cover;
    border-radius: 6px;
    margin-bottom: 8px;
`;

const PhotoCaption = styled.figcaption`
    font-size: 12px;
    color: #8c8c8c;
    padding: 6px 10px;
    background: #fafafa;
    border-top: 1px solid #f0f0f0;

    a {
        color: inherit;
        text-decoration: underline;
    }
`;

const QUESTION_TYPES = ['photo', 'kraj', 'map'];

const makeQuestion = item => {
    if (!item) return null;
    const candidateTypes = shuffleArray(QUESTION_TYPES);
    for (const type of candidateTypes) {
        if (type === 'photo' && !item.photo) continue;

        if (type === 'photo') {
            return { type: 'photo', item, options: makeOptions(item.nazev, ALL_NAMES) };
        }
        if (type === 'kraj') {
            return { type: 'kraj', item, options: makeOptions(item.kraj, KRAJ_NAMES) };
        }
        return { type: 'map', item, target: { lat: item.lat, lon: item.lon } };
    }
    return null;
};

const UnescoGame = () => {
    const { features, error } = useKrajFeatures();
    const [questionQueue, setQuestionQueue] = useState(() => shuffleArray(ITEMS));
    const [round, setRound] = useState(1);
    const [score, setScore] = useState(0);
    const [question, setQuestion] = useState(() => makeQuestion(questionQueue[0]));
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [guessPoint, setGuessPoint] = useState(null);
    const [mapResult, setMapResult] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [rounds, setRounds] = useState([]);
    const [finished, setFinished] = useState(false);

    const maxScore = TOTAL_ROUNDS * 100;

    const restart = () => {
        const nextQueue = shuffleArray(ITEMS);
        setQuestionQueue(nextQueue);
        setRound(1);
        setScore(0);
        setQuestion(makeQuestion(nextQueue[0]));
        setSelectedAnswer(null);
        setGuessPoint(null);
        setMapResult(null);
        setModalVisible(false);
        setRounds([]);
        setFinished(false);
    };

    const item = question?.item;
    const correctAnswer = question?.type === 'photo' ? item?.nazev : question?.type === 'kraj' ? item?.kraj : null;
    const isCorrectChoice = correctAnswer && selectedAnswer === correctAnswer;

    const answerOption = option => {
        if (selectedAnswer) return;
        setSelectedAnswer(option);
        const correct = option === correctAnswer;
        if (correct) setScore(s => s + 100);
        setRounds(prev => [...prev, { correct, score: correct ? 100 : 0, label: item.nazev }]);
        setModalVisible(true);
    };

    const submitMapGuess = () => {
        if (!guessPoint || mapResult) return;
        const distance = calculateDistanceKm(guessPoint, question.target);
        const roundScore = calculateBlindMapScore(distance);
        setMapResult({ distance, score: roundScore });
        setScore(s => s + roundScore);
        setRounds(prev => [
            ...prev,
            { correct: roundScore >= CORRECT_SCORE_THRESHOLD, score: roundScore, label: item.nazev },
        ]);
        setModalVisible(true);
    };

    const advanceRound = () => {
        const nextRound = round + 1;
        setModalVisible(false);
        setRound(nextRound);
        setQuestion(makeQuestion(questionQueue[nextRound - 1]));
        setSelectedAnswer(null);
        setGuessPoint(null);
        setMapResult(null);
    };

    const showFinal = () => {
        setModalVisible(false);
        setFinished(true);
    };

    if (finished || !question) {
        return <QuizFinalResult score={score} totalRounds={TOTAL_ROUNDS} rounds={rounds} onRestart={restart} />;
    }

    const renderPhoto = () => (
        <PhotoFrame>
            <PhotoImage src={item.photo} alt={item.nazev} loading="lazy" />
            {(item.photoAttribution || item.photoLicense) && (
                <PhotoCaption>
                    Foto: {item.photoAttribution || 'neuvedeno'}
                    {item.photoLicense ? ` • ${item.photoLicense}` : ''} •{' '}
                    <a
                        href={`https://commons.wikimedia.org/wiki/File:${item.photoFile}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Wikimedia Commons
                    </a>
                </PhotoCaption>
            )}
        </PhotoFrame>
    );

    const correctForModal =
        question.type === 'map' && mapResult ? mapResult.score >= CORRECT_SCORE_THRESHOLD : !!isCorrectChoice;
    const wrongVerdictForModal =
        question.type === 'map' && mapResult
            ? `${item.nazev} jsi netrefil :(`
            : selectedAnswer && !isCorrectChoice
            ? `${selectedAnswer} není správná odpověď :(`
            : null;

    return (
        <>
            <QuizPanel>
                <RoundStatus round={round} score={score} maxScore={maxScore} totalRounds={TOTAL_ROUNDS} />

                {question.type === 'photo' && (
                    <>
                        <Question>
                            <p>Česká památka UNESCO</p>
                            <h2>Která památka je na obrázku?</h2>
                            <p>Vyber správnou odpověď ze čtyř možností.</p>
                        </Question>
                        {renderPhoto()}
                        <OptionGrid>
                            {question.options.map(option => (
                                <OptionButton
                                    key={option}
                                    answer={option}
                                    correctAnswer={correctAnswer}
                                    disabled={!!selectedAnswer}
                                    selectedAnswer={selectedAnswer}
                                    onClick={() => answerOption(option)}
                                />
                            ))}
                        </OptionGrid>
                    </>
                )}

                {question.type === 'kraj' && (
                    <>
                        <Question>
                            <p>Česká památka UNESCO ({item.rok})</p>
                            <h2>{item.nazev}</h2>
                            <p>Ve kterém kraji se nachází?</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={item.nazev} label="Fotka památky" />
                        <OptionGrid>
                            {question.options.map(option => (
                                <OptionButton
                                    key={option}
                                    answer={option}
                                    correctAnswer={correctAnswer}
                                    disabled={!!selectedAnswer}
                                    selectedAnswer={selectedAnswer}
                                    onClick={() => answerOption(option)}
                                />
                            ))}
                        </OptionGrid>
                    </>
                )}

                {question.type === 'map' && (
                    <>
                        <Question>
                            <p>Slepá mapa UNESCO památek</p>
                            <h2>{item.nazev}</h2>
                            <p>Klikni do mapy přibližně tam, kde se památka nachází.</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={item.nazev} label="Fotka památky" />
                        {error && (
                            <Alert type="error" message="Mapa se nepodařila načíst" description={error.message} />
                        )}
                        <MapFrame>
                            {features ? (
                                <SvgMap
                                    features={features}
                                    guessPoint={guessPoint}
                                    targetPoint={mapResult ? question.target : null}
                                    onMapClick={mapResult ? null : setGuessPoint}
                                />
                            ) : (
                                <Alert type="info" message="Načítám mapu..." />
                            )}
                        </MapFrame>
                        {!mapResult && (
                            <Space wrap>
                                <Button type="primary" disabled={!guessPoint} onClick={submitMapGuess}>
                                    Vyhodnotit
                                </Button>
                                {guessPoint && <Tag>Tip umístěn</Tag>}
                            </Space>
                        )}
                    </>
                )}
            </QuizPanel>

            <RoundResultModal
                visible={modalVisible}
                correct={correctForModal}
                round={round}
                totalRounds={TOTAL_ROUNDS}
                onNext={advanceRound}
                onShowResult={showFinal}
                wrongVerdict={wrongVerdictForModal}
            >
                <h3>Správná odpověď</h3>
                {item.photo && <ModalPhoto src={item.photo} alt={item.nazev} loading="lazy" />}
                <p>
                    <strong style={{ fontSize: '1.2em' }}>{item.nazev}</strong>
                    <br />
                    {item.mesto} • {item.kraj} • zápis {item.rok}
                </p>
                {question.type === 'map' && mapResult && (
                    <>
                        <p>
                            Tvůj tip byl <strong>{formatDistance(mapResult.distance)}</strong> od správné polohy ·{' '}
                            <strong>{mapResult.score}</strong> bodů za kolo.
                        </p>
                        <ModalMapPreview features={features} guessPoint={guessPoint} targetPoint={question.target} />
                    </>
                )}
                {question.type !== 'map' && selectedAnswer && !isCorrectChoice && (
                    <p>
                        Tvůj tip byl: <strong>{selectedAnswer}</strong>.
                    </p>
                )}
            </RoundResultModal>
        </>
    );
};

export const Unesco = () => (
    <Content>
        <Page>
            <Header>
                <BackToModesLink />
                <h1>UNESCO památky</h1>
                <p>
                    V každé hře projdeš všech {TOTAL_ROUNDS} českých památek UNESCO v náhodném pořadí. Otázky střídají
                    poznávání podle obrázku, kraj nebo polohu na slepé mapě. Fotky a popisky pocházejí z Wikipedie a
                    Wikimedia Commons (CC).
                </p>
            </Header>
            <UnescoGame />
        </Page>
    </Content>
);
